import { TRPCClientError } from '@trpc/client'

/**
 * Transient-failure retry policy for react-query QUERIES ONLY (wired in `main.tsx`).
 *
 * On a lossy link (high latency / packet loss, e.g. clients far from the origin) a single dropped request
 * would otherwise fail the query permanently. Retry only failures a retry could actually fix — a transport
 * failure (no HTTP response reached us: network drop, DNS, CORS, abort) or a transient server-side status
 * (5xx / 408 / 429) — and NEVER a domain error: the backend maps every ErrorCode to a 4xx
 * (ERROR_CODE_TO_TRPC), so retrying NOT_LOGGED_IN / a validation code is pointless and only delays the
 * error the user must see. Mutations are deliberately NOT retried (a retried mutation could double-book or
 * double-claim), so this policy is applied to the query defaults alone.
 */
export const MAX_QUERY_RETRIES = 3

/**
 * Is this failure the kind a retry could actually fix — a transport failure (no HTTP response: network drop,
 * DNS, CORS, abort) or a transient server-side status (5xx / 408 / 429)? A domain error is NOT: the backend maps
 * every ErrorCode to a 4xx (ERROR_CODE_TO_TRPC), so retrying NOT_LOGGED_IN or a validation code only delays the
 * answer the user has to see.
 *
 * Shared by the retry policy below and the route error boundary (route-error.tsx), so a transient 503 that
 * outlasts the in-query retries auto-recovers there instead of showing a hard error — one definition of
 * "transient", so the two can't drift apart.
 */
export function isRetryableError(error: unknown): boolean {
  if (error instanceof TRPCClientError) {
    const status = (error.data as { httpStatus?: number } | null | undefined)?.httpStatus
    // No httpStatus → the fetch never got a response (network drop, DNS, CORS, aborted). Retry.
    if (status == null) return true
    // A response came back: retry only transient server-side statuses, never a domain 4xx.
    return status >= 500 || status === 408 || status === 429
  }
  // A non-tRPC error escaping the transport is unexpected/transient — give it the same bounded retries.
  return true
}

export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_QUERY_RETRIES) return false
  return isRetryableError(error)
}
